import { Activity, Clock, DollarSign, Star, TrendingUp, Zap } from 'lucide-react'

interface Props {
  agent: any
  performance: any
}

// Mertebe seviyeleri
const MERTEBE_LEVELS = ['NASUT', 'MELEKUT', 'CEBERUT', 'LAHUT', 'HAHUT']
const MERTEBE_COLORS: Record<string, string> = {
  NASUT: '#94a3b8', MELEKUT: '#3b82f6', CEBERUT: '#8b5cf6',
  LAHUT: '#f59e0b', HAHUT: '#ec4899'
}

// Gazzali 4 erdem
const VIRTUES = [
  { key: 'hikmet', label: 'Hikmet', color: '#6366f1' },
  { key: 'secaat', label: 'Secaat', color: '#10b981' },
  { key: 'iffet', label: 'Iffet', color: '#f59e0b' },
  { key: 'adalet', label: 'Adalet', color: '#ec4899' },
]

export default function AgentPerformanceTab({ agent, performance }: Props) {
  const mertebe = agent.mertebe_level ?? 'NASUT'
  const mertebeIdx = MERTEBE_LEVELS.indexOf(mertebe)
  const gazzali = agent.gazzali_scores ?? {}

  return (
    <div className="ad-perf-grid">
      {/* Stat cards */}
      <div className="ad-stats-row">
        {[
          { icon: <Activity size={16} />, val: performance.tasks_completed ?? 0, lbl: 'Tasks', color: '#6366f1' },
          { icon: <Star size={16} />, val: `${(performance.avg_quality ?? 0).toFixed(1)}/10`, lbl: 'Quality', color: '#f59e0b' },
          { icon: <Clock size={16} />, val: `${performance.response_time ?? 0}s`, lbl: 'Response', color: '#3b82f6' },
          { icon: <DollarSign size={16} />, val: `${(performance.total_earnings ?? 0).toFixed(0)}`, lbl: 'FLZ Earned', color: '#10b981' },
        ].map(s => (
          <div key={s.lbl} className="ad-stat-card">
            <div className="ad-stat-card-icon" style={{ background: `${s.color}18`, color: s.color }}>{s.icon}</div>
            <div className="ad-stat-card-body">
              <div className="ad-stat-card-val">{s.val}</div>
              <div className="ad-stat-card-lbl">{s.lbl}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Mertebe seviyesi */}
      <div className="ad-card">
        <div className="ad-card-header">
          <TrendingUp size={15} className="ad-card-icon" />
          <h3 className="ad-card-title">Mertebe Level</h3>
        </div>
        <div className="ad-mertebe-track">
          {MERTEBE_LEVELS.map((m, i) => (
            <div key={m} className={`ad-mertebe-step ${i <= mertebeIdx ? 'active' : ''}`}
              style={{ '--step-color': MERTEBE_COLORS[m] } as React.CSSProperties}>
              <div className="ad-mertebe-dot" />
              <span className="ad-mertebe-label">{m}</span>
            </div>
          ))}
        </div>
      </div>

      {/* EQ + Performance bars */}
      <div className="ad-card">
        <div className="ad-card-header">
          <Zap size={15} className="ad-card-icon" />
          <h3 className="ad-card-title">Performance Metrics</h3>
        </div>
        <div className="ad-perf-list">
          {[
            { label: 'EQ Score', pct: (agent.eq_score ?? 0) * 10, color: MERTEBE_COLORS[mertebe] },
            { label: 'Success Rate', pct: (performance.success_rate ?? 0) * 100, color: '#10b981' },
            { label: 'Avg Quality', pct: ((performance.avg_quality ?? 0) / 10) * 100, color: '#6366f1' },
          ].map(p => (
            <div key={p.label} className="ad-perf-item">
              <div className="ad-perf-label-row">
                <span>{p.label}</span>
                <span className="ad-perf-pct">{p.pct.toFixed(0)}%</span>
              </div>
              <div className="ad-perf-track">
                <div className="ad-perf-fill" style={{ width: `${Math.min(100, p.pct)}%`, background: p.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gazzali 4 Erdem */}
      <div className="ad-card">
        <div className="ad-card-header">
          <Star size={15} className="ad-card-icon" />
          <h3 className="ad-card-title">Gazzali Virtues</h3>
        </div>
        <div className="ad-virtues-grid">
          {VIRTUES.map(v => {
            const score = gazzali[v.key] ?? 0
            return (
              <div key={v.key} className="ad-virtue-item">
                <div className="ad-virtue-ring" style={{
                  background: `conic-gradient(${v.color} ${score * 10}%, var(--border-color) 0%)`
                }}>
                  <span className="ad-virtue-val">{score.toFixed(1)}</span>
                </div>
                <span className="ad-virtue-label">{v.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
